import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { aktualniJazyk, prelozit } from '../lib/i18n';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);
import { API } from '../lib/api';
import { dejToken, ulozToken, smazToken, hlidejNecinnost } from '../lib/session';
import { otiskHesla, jePodporovano } from '../lib/crypto';

// Přihlášení je nad jazykovým kontextem (odhlašuje i dřív, než se stihne
// načíst), takže se jazyk čte přímo z uložené volby.
const hlaska = (klic) => prelozit(aktualniJazyk(), klic);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(dejToken);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // GoogleCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/google-callback' || window.location.pathname === '/auth/facebook-callback') {
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else { setLoading(false); }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch { logout(); }
    finally { setLoading(false); }
  };

  const login = async (tokenOrEmail, passwordOrUser) => {
    // Support both: login(email, password) and login(token, userObj) for Google auth
    if (typeof passwordOrUser === 'object' && passwordOrUser !== null) {
      const newToken = tokenOrEmail;
      const userData = passwordOrUser;
      ulozToken(newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
      setUser(userData);
      return userData;
    }
    const email = tokenOrEmail;
    const heslo = passwordOrUser;

    // Server nemá heslo vůbec dostávat – klíč ke zprávám se odemyká právě jím,
    // takže dokud by ho viděl, mohl by ho odvodit. Pošle se jen otisk.
    //
    // Nejdřív se zeptáme na sůl. Účet, který šifrování ještě nemá zapnuté,
    // odpoví verzí 1 a přihlásí se po staru heslem.
    let sul = null;
    let verze = 1;
    if (jePodporovano()) {
      try {
        const r = await axios.get(`${API}/auth/login-salt`, { params: { email } });
        sul = r.data.auth_salt;
        verze = r.data.auth_version;
      } catch { /* když se sůl nepodaří získat, zkusíme to postaru */ }
    }

    const telo = (verze === 2 && sul)
      ? { email, auth_proof: await otiskHesla(heslo, sul) }
      : { email, password: heslo };

    const response = await axios.post(`${API}/auth/login`, telo);

    // Účet s dvoufázovým přihlášením nedostane token, ale výzvu. Přihlášení
    // vznikne až po zadání kódu z aplikace – tady tedy jen řekneme, že to ještě
    // není hotové, a formulář se zeptá na kód.
    if (response.data.two_factor_required) {
      return { two_factor_required: true, challenge: response.data.challenge };
    }

    const { token: newToken, user: userData } = response.data;
    ulozToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    // Účet se na přihlašování otiskem nepřevádí automaticky. Mobilní aplikace
    // to zatím neumí, takže by se člověk přestal přihlašovat v telefonu, aniž by
    // věděl proč. Převod se děje až při zapnutí šifrování zpráv – tam se na to
    // dá slušně upozornit a má to i smysl: teprve tehdy je co chránit.
    return userData;
  };

  /** Druhý krok přihlášení: výzva a kód z aplikace v telefonu. */
  const dokonciDvoufazove = async (challenge, code) => {
    const { data } = await axios.post(`${API}/auth/login/2fa`, { challenge, code });
    ulozToken(data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const register = async (userData) => {
    // Registrace zůstává heslem. Účet se převede na přihlašování otiskem až
    // při zapnutí šifrování – do té doby funguje i mobilní aplikace. Server
    // také díky tomu může při zakládání účtu zkontrolovat sílu hesla.
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser } = response.data;
    ulozToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);
    return newUser;
  };

  const logout = useCallback((duvod) => {
    smazToken();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    if (duvod === 'necinnost') {
      toast(hlaska('odhl.necinnost'), { description: hlaska('odhl.necinnostPopis') });
    }
  }, []);

  // Odhlášení po nečinnosti. Běží jen když je někdo přihlášený – jinak
  // by časovače tikaly zbytečně.
  const varovaniZobrazeno = useRef(false);
  useEffect(() => {
    if (!token) return undefined;
    varovaniZobrazeno.current = false;
    return hlidejNecinnost(
      () => {
        if (varovaniZobrazeno.current) return;
        varovaniZobrazeno.current = true;
        toast(hlaska('odhl.zaChvili'), { description: hlaska('odhl.zaChviliPopis') });
      },
      () => logout('necinnost'),
    );
  }, [token, logout]);

  const updateProfile = async (updates) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(updates)) {
      if (value !== undefined && value !== null && key !== 'custom_avatar') params.append(key, value);
    }
    const response = await axios.put(`${API}/auth/profile?${params.toString()}`);
    setUser(response.data);
    return response.data;
  };

  const deleteAccount = async () => {
    await axios.delete(`${API}/auth/profile`);
    logout();
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, dokonciDvoufazove, register, logout, updateProfile, deleteAccount,
      isAuthenticated: !!user, isAdmin: user?.role === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
